import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { postSimulacao, putSalvarDados } from '@service'
import { Payload } from '@store/dadosPessoaisSlice/type'
import { minimoSaldo } from '@util'

import { PayloadSemAutorizacao, SimulacaoResponse, SimulacaoState, StatusError } from './type'

export const initialState: SimulacaoState = {
  firstSimulation: true,
  periodos: 15,
  loading: false,
  showResults: true,
  error: false,
  statusError: {
    data: '',
  },
  maxParcela: 15,
  saldo: undefined,
  data: {
    guid: '',
    status: '',
    valores: {
      parcelas: [],
      percentValorIof: 0,
      percentValorJuros: 0,
      percentValorLiquido: 0,
      txCetAnual: 0,
      txCetMensal: 0,
      txJurosEfetivaAnual: 0,
      txJurosEfetivaMensal: 0,
      valorFinanciado: 0,
      valorIof: 0,
      valorJuros: 0,
      valorLiquido: 0,
      valorTotalParcelas: 0,
    },
  },
}

export const postSimulacaoThunk = createAsyncThunk(
  'dadosPessoais/simulacaoPost',
  async (
    {
      cpf,
      dtNascimento,
      nome,
      telefone,
      email,
      erro,
      callback,
      token,
      utm,
      autorizaContato,
      saldoFgts,
    }: Payload,
    { rejectWithValue },
  ) => {
    try {
      const { data } = await postSimulacao({
        cpf,
        dataNascimento: dtNascimento,
        nome,
        telefone,
        email,
        token,
        utm,
        autorizaContato,
        saldoFgts,
      })

      if (callback) callback()

      return data
    } catch (error: unknown) {
      const e = error as Exception
      const msg = e?.data?.status || ''
      if (erro) erro()
      return rejectWithValue({ data: msg })
    }
  },
)

export const putSimulacaoThunk = createAsyncThunk(
  'simulacaoSemAutorizacao/getSimulacaoAutorizacao',
  async (
    { guid, qtPeriodos = 15, saldoFgts = minimoSaldo, erro, callback }: PayloadSemAutorizacao,
    { rejectWithValue },
  ) => {
    try {
      const { data } = await putSalvarDados({
        guid,
        data: {
          saldoFgts,
          qtPeriodos,
        },
      })

      if (callback) callback(data?.valores?.parcelas?.length || 10)
      return data
    } catch (error: unknown) {
      const e = error as Exception
      const msg = e?.data?.status || ''
      if (erro) erro(msg as string)
      return rejectWithValue({ data: msg })
    }
  },
)

const simulacao = createSlice({
  name: 'simulacao',
  initialState,
  reducers: {
    setPeriodos: (state, action: PayloadAction<number>) => {
      state.periodos = action.payload
      state.showResults = false
    },
    setSaldo: (state, action: PayloadAction<number>) => {
      state.saldo = action.payload
    },
    setSimulacao: (state, action: PayloadAction<SimulacaoResponse>) => {
      state.data = action.payload
    },
    setFirstSimulation: (state, action: PayloadAction<boolean>) => {
      state.firstSimulation = action.payload
    },
    setMaxNuParcela: (state, action: PayloadAction<number>) => {
      state.maxParcela = action.payload
    },
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(postSimulacaoThunk.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false
        state.showResults = true
        state.error = false
        state.maxParcela = action.payload.valores.parcelas.length
        state.periodos = action.payload.valores.parcelas.length
        if (state.firstSimulation) {
          state.firstSimulation = false
        }
      })
      .addCase(postSimulacaoThunk.pending, (state) => {
        state.loading = true
        state.showResults = false
        state.error = false
      })
      .addCase(postSimulacaoThunk.rejected, (state, action) => {
        state.error = true
        state.loading = false
        state.showResults = true
        state.statusError = (action.payload as StatusError) ?? { data: '' }
      })

    builder
      .addCase(putSimulacaoThunk.fulfilled, (state, action) => {
        state.data = action.payload
        state.loading = false
        state.showResults = true
        state.error = false
      })
      .addCase(putSimulacaoThunk.pending, (state) => {
        state.loading = true
        state.showResults = false
        state.error = false
      })
      .addCase(putSimulacaoThunk.rejected, (state, action) => {
        state.error = true
        state.loading = false
        state.showResults = true
        state.statusError = (action.payload as StatusError) ?? { data: '' }
      })
  },
})

export const { setPeriodos, setSaldo, setSimulacao, reset, setFirstSimulation, setMaxNuParcela } =
  simulacao.actions

export default simulacao.reducer
